import React from 'react';
import {
  buildRunTraceHref,
  downloadEvalPackExport,
  fetchEvalPackDetailData,
  fetchEvalPackRunDetailData,
  importEvalPackResults,
  runEvalPackAutoChecks,
} from '../data';
import { RuntimeRiskCoverageStatusPanel } from './RuntimeScenarioCandidateCard';

function formatDateTime(value) {
  if (!value) {
    return 'Not available yet';
  }
  return new Date(value).toLocaleString();
}

function validationModeLabel(mode) {
  switch (mode) {
    case 'auto_checkable':
      return 'Auto-checkable';
    case 'human_review_required':
      return 'Needs human review';
    case 'external_runner_required':
      return 'Needs external evaluation';
    default:
      return mode || 'Unknown';
  }
}

function validationPostureLabel(posture) {
  switch (posture) {
    case 'in_app_auto_check':
      return 'Auto-checkable';
    case 'human_review_required':
      return 'Human review';
    case 'external_evaluation_required':
      return 'External evaluation';
    case 'diagnostic_only':
      return 'Diagnostic only';
    default:
      return posture ? posture.replaceAll('_', ' ') : 'Reference guidance';
  }
}

function expectedMovementLabel(direction) {
  switch (direction) {
    case 'decrease':
      return 'Should decrease';
    case 'increase':
      return 'Should increase';
    case 'stabilize':
      return 'Should stabilize';
    default:
      return direction ? direction.replaceAll('_', ' ') : 'Expected movement pending';
  }
}

function ReferenceEvalPackPanel({ reference }) {
  if (!reference) {
    return null;
  }
  return (
    <section className="detail-section">
      <h3>Reference validation guidance</h3>
      <div className="detail-card eval-pack-reference-card">
        <div className="workflow-mode-header">
          <strong>{reference.title}</strong>
          <span className="chip passive-meta-chip">{validationPostureLabel(reference.validation_posture)}</span>
        </div>
        <p>{reference.partner_explanation}</p>
        <div className="stack compact-stack eval-pack-guidance">
          {reference.trace_review_guidance?.length ? (
            <div className="eval-pack-guidance-block">
              <strong>Trace-review guidance</strong>
              <ol className="eval-pack-guidance-list">
                {reference.trace_review_guidance.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </div>
          ) : null}
          {reference.overclaim_limits?.length ? (
            <div className="eval-pack-guidance-block">
              <strong>Overclaim limits</strong>
              <ul className="eval-pack-guidance-list">
                {reference.overclaim_limits.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function roleLabel(role) {
  switch (role) {
    case 'near_miss':
      return 'Near miss';
    case 'recovered':
      return 'Recovered';
    case 'control':
      return 'Control';
    case 'failure':
    default:
      return 'Failure';
  }
}

function statusLabel(status) {
  switch (status) {
    case 'needs_labels':
      return 'Needs labels';
    case 'ready':
      return 'Ready';
    case 'validated':
      return 'Validated';
    case 'archived':
      return 'Archived';
    default:
      return status || 'Unknown';
  }
}

function resultStatusLabel(status) {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'partial':
      return 'Partial';
    case 'failed':
      return 'Failed';
    default:
      return status || 'Unknown';
  }
}

function sourceTypeLabel(sourceType) {
  switch (sourceType) {
    case 'manual_review_import':
      return 'Manual import';
    case 'external_eval_import':
      return 'External import';
    case 'internal_auto_check':
      return 'Internal auto-check';
    default:
      return sourceType || 'Unknown';
  }
}

function outcomeLabel(outcome) {
  switch (outcome) {
    case 'pass':
      return 'Pass';
    case 'fail':
      return 'Fail';
    case 'unclear':
      return 'Unclear';
    case 'unlabeled':
    default:
      return 'Unlabeled';
  }
}

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function validationResultVerdict(summary) {
  if (!summary) {
    return null;
  }

  const decisiveCount = summary.decisive_item_count || 0;
  const labeledCount = summary.labeled_item_count || 0;
  const passRate = summary.pass_rate;

  if (summary.status === 'failed') {
    return {
      tone: 'alert',
      label: 'Run status',
      title: 'Auto-check run failed',
      description: 'The validation run did not complete cleanly, so treat this result as operationally failed rather than evidence of behavior.',
    };
  }

  if (decisiveCount <= 0 || typeof passRate !== 'number') {
    return {
      tone: 'muted',
      label: 'Finding support',
      title: 'Inconclusive',
      description: `The latest run labeled ${labeledCount} items, but it did not produce enough decisive outcomes to confirm or clear the finding.`,
    };
  }

  if (passRate >= 1) {
    return {
      tone: 'success',
      label: 'Finding support',
      title: 'Expected behavior met',
      description: `All decisive cases met the expected behavior. The latest checked evidence does not reproduce this finding.`,
    };
  }

  if (passRate <= 0) {
    return {
      tone: 'alert',
      label: 'Finding support',
      title: 'Finding confirmed',
      description: `0 of ${decisiveCount} decisive cases met the expected behavior. The checked evidence supports this finding.`,
    };
  }

  const passedCount = Math.round(passRate * decisiveCount);
  return {
    tone: 'warn',
    label: 'Finding support',
    title: 'Finding partially confirmed',
    description: `${passedCount} of ${decisiveCount} decisive cases met the expected behavior. Some checked evidence still supports this finding.`,
  };
}

function expectedBehaviorHeading(validationMode) {
  if (validationMode === 'human_review_required') {
    return 'Human review guide';
  }
  if (validationMode === 'external_runner_required') {
    return 'External evaluation guide';
  }
  return 'Automatic check';
}

function automaticCheckGuidance(expectedBehavior) {
  const checks = expectedBehavior?.checks || [];
  return {
    description: expectedBehavior?.description || 'This case is checked automatically using deterministic runtime rules.',
    steps: [],
    criteria: checks.map((check) => {
      switch (check.kind) {
        case 'step_order':
          return 'Confirm that a validation step appears before the risky tool or external action.';
        case 'validation_presence':
          return 'Confirm that a risky step has a nearby validation step before it runs.';
        case 'handoff_gate':
          return 'Confirm that escalation happens only after enough retries or a terminal failure signal.';
        default:
          return `Automatic rule: ${check.rule || check.kind || 'unknown check'}.`;
      }
    }),
  };
}

function manualReviewGuidance(findingType, expectedBehavior) {
  if (findingType === 'brittle_retrieval_strategy') {
    return {
      description: 'Goal: decide whether fallback retrieval genuinely rescued a weak earlier retrieval attempt.',
      steps: [
        'Open the trace for this run.',
        'Find the earlier retrieval attempt and the later fallback retrieval.',
        'Compare the evidence returned before and after the fallback.',
        'Decide whether the fallback materially improved the usefulness or relevance of the evidence.',
      ],
      criteria: [
        'Mark this as a useful recovery if the first retrieval was weak and the fallback clearly returned better evidence.',
        'Do not mark it as a recovery if the first retrieval was already good enough or the fallback did not meaningfully improve the evidence.',
        'If the trace is too ambiguous to judge, leave the case unlabeled for now.',
      ],
    };
  }
  if (findingType === 'weak_answer_grounding_risk') {
    return {
      description: 'Goal: decide whether the final answer stayed grounded even though the supporting evidence was weak.',
      steps: [
        'Open the trace for this run.',
        'Review the evidence or evidence summaries that were available before the answer was generated.',
        'Compare the final answer against that evidence.',
        'Use an external evaluator only if the trace alone is not enough to make the call.',
      ],
      criteria: [
        'Pass if the answer stays faithful to the available evidence and avoids unsupported claims.',
        'Fail if the answer makes claims that are stronger than the evidence supports or invents details.',
        'If the evidence context is too incomplete to judge confidently, leave the case unlabeled for now.',
      ],
    };
  }
  return {
    description: expectedBehavior?.description || 'Review this case and decide whether the observed behavior matches the intended behavior.',
    steps: expectedBehavior?.review_instructions || [],
    criteria: expectedBehavior?.judgment_criteria || [],
  };
}

function behaviorGuidance(item, findingType) {
  if (!item.expected_behavior) {
    return null;
  }
  if (item.validation_mode === 'auto_checkable') {
    return automaticCheckGuidance(item.expected_behavior);
  }
  return manualReviewGuidance(findingType, item.expected_behavior);
}

function evalPackRuleSummary(packDetail) {
  const reference = packDetail?.reference || {};
  const representativeItem = (packDetail?.items || []).find((item) => item.expected_behavior) || (packDetail?.items || [])[0] || null;
  const guidance = representativeItem ? behaviorGuidance(representativeItem, packDetail.finding_type) : null;
  const metric = reference.compare_metric_label || reference.compare_metric?.replaceAll('_', ' ') || packDetail?.finding_title;
  const movement = expectedMovementLabel(reference.expected_movement_direction);

  return {
    title: reference.title || packDetail?.finding_title || 'Validation rule',
    metric,
    movement,
    posture: validationPostureLabel(reference.validation_posture || representativeItem?.validation_mode),
    description:
      guidance?.description
      || reference.partner_explanation
      || packDetail?.coverage_note
      || 'This pack checks whether the prepared evidence meets the expected behavior for the finding.',
    criteria: guidance?.criteria || [],
    representativeItemTitle: representativeItem?.title || null,
  };
}

function EvalPackRuleSummaryCard({ summary }) {
  if (!summary) {
    return null;
  }

  return (
    <section className="detail-card eval-pack-rule-card">
      <div className="workflow-mode-header">
        <div>
          <span className="summary-card-label">Check being run</span>
          <strong>{summary.title}</strong>
        </div>
        <span className="chip passive-meta-chip">{summary.posture}</span>
      </div>
      <p>{summary.description}</p>
      <div className="detail-metrics-grid eval-pack-rule-grid">
        <div>
          <span>Metric</span>
          <strong>{summary.metric}</strong>
        </div>
        <div>
          <span>Expected movement</span>
          <strong>{summary.movement}</strong>
        </div>
        {summary.representativeItemTitle ? (
          <div>
            <span>Example case</span>
            <strong>{summary.representativeItemTitle}</strong>
          </div>
        ) : null}
      </div>
      {summary.criteria.length ? (
        <div className="eval-pack-guidance-block">
          <strong>Rule criteria</strong>
          <ul className="eval-pack-guidance-list">
            {summary.criteria.slice(0, 3).map((criterion) => (
              <li key={criterion}>{criterion}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function reviewContextHeading(validationMode) {
  if (validationMode === 'external_runner_required') {
    return 'Review context for external evaluation';
  }
  return 'Review context';
}

function focusLabel(focus) {
  if (!focus) {
    return 'Not available yet';
  }
  const parts = [focus.label];
  if (focus.kind) {
    parts.push(focus.kind);
  }
  if (focus.step_id) {
    parts.push(`Step ${focus.step_id}`);
  }
  if (focus.source_run_key) {
    parts.push(`Run ${focus.source_run_key}`);
  }
  return parts.join(' · ');
}

function summaryLabel(key) {
  switch (key) {
    case 'run_key':
      return 'Run key';
    case 'step_label':
      return 'Step';
    case 'step_kind':
      return 'Kind';
    case 'step_id':
      return 'Step ID';
    case 'step_index':
      return 'Step index';
    case 'event_type':
      return 'Event';
    case 'source_name':
      return 'Source';
    case 'relevant_count':
    case 'relevantCount':
      return 'Relevant results';
    case 'total_documents':
    case 'totalDocuments':
      return 'Documents';
    case 'result_count':
    case 'resultCount':
      return 'Result count';
    case 'top_score':
      return 'Top score';
    case 'user_query':
      return 'User query';
    case 'tool_query':
      return 'Tool query';
    default:
      return key.replace(/_/g, ' ');
  }
}

function summaryEntries(summary) {
  if (!summary || typeof summary !== 'object') {
    return [];
  }
  function formatSummaryValue(value) {
    if (Array.isArray(value)) {
      if (!value.length) {
        return '[]';
      }
      if (value.every((entry) => entry == null || ['string', 'number', 'boolean'].includes(typeof entry))) {
        return value.map((entry) => String(entry)).join(', ');
      }
      return JSON.stringify(value);
    }
    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }
  const orderedKeys = [
    'run_key',
    'step_label',
    'step_kind',
    'step_id',
    'step_index',
    'event_type',
    'source_name',
    'relevant_count',
    'relevantCount',
    'total_documents',
    'totalDocuments',
    'result_count',
    'resultCount',
    'top_score',
    'user_query',
    'tool_query',
  ];
  const seen = new Set();
  const entries = [];

  orderedKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(summary, key) && summary[key] != null) {
      seen.add(key);
      entries.push([summaryLabel(key), formatSummaryValue(summary[key])]);
    }
  });

  Object.entries(summary).forEach(([key, value]) => {
    if (!seen.has(key) && value != null) {
      entries.push([summaryLabel(key), formatSummaryValue(value)]);
    }
  });

  return entries;
}

function SummaryBlock({ title, summary }) {
  const entries = summaryEntries(summary);
  if (!entries.length) {
    return null;
  }
  return (
    <div className="eval-pack-guidance-block">
      <strong>{title}</strong>
      <ul className="eval-pack-guidance-list">
        {entries.map(([label, value]) => (
          <li key={`${title}-${label}`}>
            <strong>{label}:</strong> {value}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResultPayloadBlock({ title, payload }) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const evaluations = Array.isArray(payload.evaluations) ? payload.evaluations : [];
  const supplementalPayload = Object.fromEntries(Object.entries(payload).filter(([key]) => key !== 'evaluations'));
  const entries = summaryEntries(supplementalPayload);
  if (!evaluations.length && !entries.length) {
    return null;
  }

  function automaticEvaluationTitle(evaluation) {
    switch (evaluation.kind) {
      case 'step_order':
      case 'validation_presence':
        return 'Validation before risky step';
      case 'handoff_gate':
        return 'Escalation gate';
      default:
        return evaluation.rule || evaluation.kind || 'Automatic evaluation';
    }
  }

  function automaticEvaluationExplanation(evaluation) {
    switch (evaluation.kind) {
      case 'step_order':
      case 'validation_presence':
        return evaluation.passed
          ? 'A qualifying validation step was found before the risky step.'
          : 'No qualifying validation step was found before the risky step.';
      case 'handoff_gate':
        return evaluation.passed
          ? 'The trace showed a prior retry or terminal failure before the handoff.'
          : 'The trace did not show enough retry or failure evidence before the handoff.';
      default:
        return evaluation.passed
          ? 'This deterministic auto-check passed.'
          : 'This deterministic auto-check failed.';
    }
  }

  return (
    <div className="eval-pack-guidance-block">
      <strong>{title}</strong>
      {evaluations.length ? (
        <div className="stack compact-stack">
          <strong>Automatic evaluations</strong>
          {evaluations.map((evaluation, index) => (
            <div className="detail-card" key={`${title}-evaluation-${evaluation.kind || 'unknown'}-${index}`}>
              <div className="workflow-mode-header">
                <strong>{automaticEvaluationTitle(evaluation)}</strong>
                <span className={`chip ${evaluation.passed ? 'chip-emphasis' : ''}`}>
                  {evaluation.passed ? 'Passed' : 'Failed'}
                </span>
              </div>
              <p>{automaticEvaluationExplanation(evaluation)}</p>
              {evaluation.rule ? (
                <p>
                  <strong>Rule:</strong> {evaluation.rule}
                </p>
              ) : null}
              {evaluation.target_step_id ? (
                <p>
                  <strong>Target step:</strong> {evaluation.target_step_id}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {entries.length ? (
        <ul className="eval-pack-guidance-list">
          {entries.map(([label, value]) => (
            <li key={`${title}-${label}`}>
              <strong>{label}:</strong> {value}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function selectedOutcomeDetail(item, itemResult) {
  if (!itemResult?.raw_outcome_label) {
    return null;
  }
  if (item.validation_mode === 'auto_checkable') {
    return `Auto-check verdict ${itemResult.raw_outcome_label}`;
  }
  return `Original label ${itemResult.raw_outcome_label}`;
}

function selectedOutcomeExplanation(item, itemResult) {
  const evaluations = itemResult?.source_payload?.evaluations;
  if (item.validation_mode === 'auto_checkable' && Array.isArray(evaluations) && evaluations.length) {
    const passedAll = evaluations.every((evaluation) => evaluation.passed);
    return passedAll
      ? 'This case satisfied every deterministic rule configured for the auto-check.'
      : 'This case failed at least one deterministic rule configured for the auto-check.';
  }
  if (typeof itemResult?.source_payload?.reason === 'string' && itemResult.source_payload.reason) {
    return itemResult.source_payload.reason;
  }
  return null;
}

function ReviewContextBlock({ reviewContext }) {
  return (
    <div className="stack compact-stack eval-pack-guidance">
      <strong>{reviewContextHeading(reviewContext.validation_mode)}</strong>
      <div className="eval-pack-guidance-block">
        <strong>Goal</strong>
        <p>{reviewContext.review_goal}</p>
      </div>
      <div className="eval-pack-guidance-block">
        <strong>Why this case was selected</strong>
        <p>{reviewContext.why_this_case_was_selected}</p>
      </div>
      <div className="eval-pack-guidance-block">
        <strong>Primary focus</strong>
        <p>{focusLabel(reviewContext.primary_focus)}</p>
      </div>
      <div className="eval-pack-guidance-block">
        <strong>What changed</strong>
        <p>{reviewContext.what_changed_summary}</p>
      </div>
      {reviewContext.comparison_focuses?.length ? (
        <div className="eval-pack-guidance-block">
          <strong>What to compare</strong>
          <ul className="eval-pack-guidance-list">
            {reviewContext.comparison_focuses.map((focus) => (
              <li key={`${focus.run_id}-${focus.step_id || focus.label}`}>{focusLabel(focus)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <SummaryBlock title="Before" summary={reviewContext.before_summary} />
      <SummaryBlock title="After" summary={reviewContext.after_summary} />
      <div className="eval-pack-guidance-block">
        <strong>What to do</strong>
        <ol className="eval-pack-guidance-list">
          {reviewContext.decision_instructions.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
      <div className="eval-pack-guidance-block">
        <strong>Decision prompt</strong>
        <p>{reviewContext.decision_prompt}</p>
      </div>
      <div className="eval-pack-guidance-block">
        <strong>Allowed outcomes</strong>
        <div className="chip-row">
          {reviewContext.allowed_outcomes.map((outcome) => (
            <span className="chip" key={outcome}>
              {outcome}
            </span>
          ))}
        </div>
      </div>
      {reviewContext.can_leave_unlabeled ? (
        <p className="overview-note">You can leave this item unlabeled if the persisted evidence is too ambiguous to judge confidently.</p>
      ) : null}
    </div>
  );
}

export default function EvalPackDetailView({ selectedSystem, evalPackDetail, onPackDetailChange = null }) {
  const [packDetail, setPackDetail] = React.useState(evalPackDetail);
  const [selectedResultRun, setSelectedResultRun] = React.useState(evalPackDetail?.selected_result_run || null);
  const [actionError, setActionError] = React.useState(null);
  const [actionSuccess, setActionSuccess] = React.useState(null);
  const [importingResults, setImportingResults] = React.useState(false);
  const [runningAutoChecks, setRunningAutoChecks] = React.useState(false);
  const [loadingRunId, setLoadingRunId] = React.useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    setPackDetail(evalPackDetail);
    setSelectedResultRun(evalPackDetail?.selected_result_run || null);
    setActionError(null);
    setActionSuccess(null);
    setImportingResults(false);
    setRunningAutoChecks(false);
    setLoadingRunId(null);
  }, [evalPackDetail]);

  async function refreshPackDetail(successMessage, preferredRunId = null) {
    const refreshedPack = await fetchEvalPackDetailData(packDetail.id);
    setPackDetail(refreshedPack);
    onPackDetailChange?.(refreshedPack);
    if (preferredRunId) {
      const refreshedRun = await fetchEvalPackRunDetailData(preferredRunId);
      setSelectedResultRun(refreshedRun);
    } else {
      setSelectedResultRun(refreshedPack.selected_result_run || null);
    }
    setActionSuccess(successMessage);
  }

  async function handleExportJson(event) {
    event.preventDefault();
    setActionError(null);
    setActionSuccess(null);
    try {
      await downloadEvalPackExport(packDetail.id);
    } catch (error) {
      console.error(error);
      setActionError(error instanceof Error ? error.message : 'Failed to export the validation pack.');
    }
  }

  async function handleSelectRun(runId) {
    if (!runId || selectedResultRun?.id === runId) {
      return;
    }
    setLoadingRunId(runId);
    setActionError(null);
    setActionSuccess(null);
    try {
      const runDetail = await fetchEvalPackRunDetailData(runId);
      setSelectedResultRun(runDetail);
    } catch (error) {
      console.error(error);
      setActionError(error instanceof Error ? error.message : 'Failed to load the selected validation result run.');
    } finally {
      setLoadingRunId(null);
    }
  }

  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setImportingResults(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const importedRun = await importEvalPackResults(packDetail.id, payload);
      await refreshPackDetail('Validation results imported.', importedRun.id);
    } catch (error) {
      console.error(error);
      if (error instanceof SyntaxError) {
        setActionError('Selected file is not valid JSON.');
      } else {
        setActionError(error instanceof Error ? error.message : 'Failed to import validation results.');
      }
    } finally {
      setImportingResults(false);
    }
  }

  async function handleRunAutoChecks() {
    setRunningAutoChecks(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const run = await runEvalPackAutoChecks(packDetail.id);
      await refreshPackDetail('Automatic validation checks completed.', run.id);
    } catch (error) {
      console.error(error);
      setActionError(error instanceof Error ? error.message : 'Failed to run automatic validation checks.');
    } finally {
      setRunningAutoChecks(false);
    }
  }

  const selectedRunResultsByItemId = new Map((selectedResultRun?.item_results || []).map((itemResult) => [itemResult.eval_pack_item_id, itemResult]));
  const hasAutoCheckableItems = (packDetail?.validation_mode_counts?.auto_checkable || 0) > 0;
  const latestVerdict = validationResultVerdict(packDetail.latest_result_summary);
  const ruleSummary = evalPackRuleSummary(packDetail);

  return (
    <section className="content-grid">
      <aside className="panel side-panel">
        <p className="detail-kicker">Validation pack</p>
        <h2>{selectedSystem.name}</h2>
        <div className="eval-pack-side-rule">
          <span>Checked rule</span>
          <strong>{ruleSummary.title}</strong>
          <p>
            {ruleSummary.metric} · {ruleSummary.movement}
          </p>
        </div>
        <div className="stack compact-stack">
          <a className="finding-link" href={`#/systems/${selectedSystem.id}/findings/${evalPackDetail.finding_id}`}>
            Back to finding
          </a>
          {packDetail.workflow_id ? (
            <a className="finding-link" href={`#/systems/${selectedSystem.id}/workflows/${packDetail.workflow_id}`}>
              Open workflow
            </a>
          ) : null}
          <a className="finding-link" href={`#/systems/${selectedSystem.id}/findings`}>
            Back to findings
          </a>
          <a className="finding-link" href={`/eval-packs/${packDetail.id}/export`} onClick={handleExportJson}>
            Export JSON
          </a>
          <button
            className="secondary-button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importingResults}
          >
            {importingResults ? 'Importing…' : 'Import results JSON'}
          </button>
          {hasAutoCheckableItems ? (
            <button className="secondary-button" type="button" onClick={handleRunAutoChecks} disabled={runningAutoChecks}>
              {runningAutoChecks ? 'Running checks…' : 'Run auto-checks'}
            </button>
          ) : null}
          <input
            ref={fileInputRef}
            accept="application/json"
            hidden
            onChange={handleImportFile}
            type="file"
          />
        </div>
        {actionSuccess ? <p>{actionSuccess}</p> : null}
        {actionError ? <p className="overview-note">{actionError}</p> : null}
      </aside>

      <div className="panel detail-panel eval-pack-detail-panel">
        <p className="detail-kicker">Prepared targeted validation</p>
        <h2>{packDetail.finding_title}</h2>
        <p className="panel-copy">{packDetail.coverage_note}</p>

        <EvalPackRuleSummaryCard summary={ruleSummary} />

        <div className="detail-summary-grid eval-pack-summary-grid">
          <div className="context-banner eval-pack-summary-card">
            <span className="eval-pack-summary-label">Pack status</span>
            <strong className="eval-pack-summary-value">{statusLabel(packDetail.status)}</strong>
          </div>
          <div className="context-banner eval-pack-summary-card">
            <span className="eval-pack-summary-label">Finding type</span>
            <strong className="eval-pack-summary-value eval-pack-summary-value-code">{packDetail.finding_type}</strong>
          </div>
          <div className="context-banner eval-pack-summary-card">
            <span className="eval-pack-summary-label">Expectation</span>
            <strong className="eval-pack-summary-value">{packDetail.expectation_type}</strong>
          </div>
          <div className="context-banner eval-pack-summary-card">
            <span className="eval-pack-summary-label">Attached runs</span>
            <strong className="eval-pack-summary-value">{packDetail.result_run_count}</strong>
          </div>
          <div className="context-banner eval-pack-summary-card">
            <span className="eval-pack-summary-label">Updated</span>
            <strong className="eval-pack-summary-value">{formatDateTime(packDetail.updated_at)}</strong>
          </div>
        </div>

        <ReferenceEvalPackPanel reference={packDetail.reference} />

        {packDetail.external_evaluation_coverage ? (
          <section className="detail-section">
            <h3>External evaluation coverage</h3>
            <RuntimeRiskCoverageStatusPanel coverage={packDetail.external_evaluation_coverage} />
          </section>
        ) : null}

        <section className="detail-section">
          <h3>Preparedness summary</h3>
          <div className="detail-metrics-grid">
            <div className="detail-card detail-stat-card">
              <strong>{packDetail.role_counts.failure}</strong>
              <p>Failure items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{packDetail.role_counts.near_miss}</strong>
              <p>Near-miss items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{packDetail.role_counts.recovered}</strong>
              <p>Recovered items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{packDetail.role_counts.control}</strong>
              <p>Control items</p>
            </div>
          </div>
          <div className="chip-row eval-pack-mode-row">
            <span className="chip passive-meta-chip">Auto-checkable {packDetail.validation_mode_counts.auto_checkable}</span>
            <span className="chip passive-meta-chip">Needs human review {packDetail.validation_mode_counts.human_review_required}</span>
            <span className="chip passive-meta-chip">Needs external evaluation {packDetail.validation_mode_counts.external_runner_required}</span>
          </div>
        </section>

        <section className="detail-section">
          <h3>Validation results</h3>
          {packDetail.latest_result_summary ? (
            <div className="stack compact-stack">
              {latestVerdict ? (
                <div className={`detail-card validation-verdict-card validation-verdict-card-${latestVerdict.tone}`}>
                  <span className="summary-card-label">{latestVerdict.label}</span>
                  <strong>{latestVerdict.title}</strong>
                  <p>{latestVerdict.description}</p>
                </div>
              ) : null}
              <div className="detail-metrics-grid">
                <div className="detail-card detail-stat-card">
                  <strong>{resultStatusLabel(packDetail.latest_result_summary.status)}</strong>
                  <p>Latest run</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{formatPercent(packDetail.latest_result_summary.pass_rate)}</strong>
                  <p>Pass rate</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{packDetail.latest_result_summary.labeled_item_count}</strong>
                  <p>Labeled items</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{packDetail.latest_result_summary.decisive_item_count}</strong>
                  <p>Decisive items</p>
                </div>
              </div>
              <div className="chip-row">
                {packDetail.result_source_types.map((sourceType) => (
                  <span className="chip" key={sourceType}>
                    {sourceTypeLabel(sourceType)}
                  </span>
                ))}
              </div>
              {selectedResultRun ? (
                <div className="detail-card">
                  <div className="workflow-mode-header">
                    <strong>Selected result run</strong>
                    <span className="chip passive-meta-chip">{resultStatusLabel(selectedResultRun.status)}</span>
                  </div>
                  <p>
                    {sourceTypeLabel(selectedResultRun.source_type)}
                    {selectedResultRun.source_name ? ` · ${selectedResultRun.source_name}` : ''}
                    {selectedResultRun.source_ref ? ` · ${selectedResultRun.source_ref}` : ''}
                  </p>
                  <p>
                    Executed {formatDateTime(selectedResultRun.executed_at)} · Imported {formatDateTime(selectedResultRun.imported_at)}
                  </p>
                  {selectedResultRun.summary?.auto_check_evaluated_item_count ? (
                    <p>Auto-checked items {selectedResultRun.summary.auto_check_evaluated_item_count}</p>
                  ) : null}
                </div>
              ) : null}
              {packDetail.result_runs.length ? (
                <div className="stack compact-stack bounded-secondary-collection" data-testid="eval-pack-result-runs-collection">
                  {packDetail.result_runs.map((run) => (
                    <button
                      className="detail-card workflow-link-card"
                      key={run.id}
                      onClick={() => handleSelectRun(run.id)}
                      type="button"
                    >
                      <strong>
                        {sourceTypeLabel(run.source_type)}
                        {run.source_name ? ` · ${run.source_name}` : ''}
                      </strong>
                      <p>
                        {resultStatusLabel(run.status)} · Pass rate {formatPercent(run.pass_rate)} · Labeled {run.labeled_item_count}
                      </p>
                      <p>
                        Executed {formatDateTime(run.executed_at)}
                        {loadingRunId === run.id ? ' · Loading…' : ''}
                      </p>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No validation results attached yet</strong>
              <p>This pack is still reviewable from its prepared items and review context. Attach result runs when you are ready to record outcome evidence.</p>
            </div>
          )}
        </section>

        <section className="detail-section">
          <h3>Pack items</h3>
          {packDetail.items.length ? (
            <div className="stack compact-stack">
              {packDetail.items.map((item) => {
                const guidance = behaviorGuidance(item, packDetail.finding_type);
                const reviewContext = item.review_context
                  ? { ...item.review_context, validation_mode: item.validation_mode }
                  : null;
                const traceHref = item.source_run_id
                  ? buildRunTraceHref({
                      systemId: selectedSystem.id,
                      runId: item.source_run_id,
                      primaryStepId: reviewContext?.trace_focus?.primary_step_id,
                      comparisonStepIds: reviewContext?.trace_focus?.comparison_step_ids || [],
                    })
                  : null;
                const itemResult = selectedRunResultsByItemId.get(item.id);
                return (
                  <article className="detail-card eval-pack-item-card" key={item.id}>
                    <div className="workflow-mode-header">
                      <strong>{item.title}</strong>
                      <div className="eval-pack-item-meta">
                        <span>{roleLabel(item.role)}</span>
                        <span>{validationModeLabel(item.validation_mode)}</span>
                        {itemResult ? <span>Outcome {outcomeLabel(itemResult.normalized_outcome)}</span> : null}
                      </div>
                    </div>
                    <p>{item.summary}</p>
                    {item.input_payload?.run ? (
                      <div className="chip-row">
                        <span className="chip">Run status {item.input_payload.run.status}</span>
                        <span className="chip">
                          Run key {item.input_payload.run.source_run_key || String(item.source_run_id).slice(0, 12)}
                        </span>
                        {traceHref ? (
                          <a
                            className="chip chip-emphasis"
                            href={traceHref}
                            rel="noreferrer"
                            target="_blank"
                          >
                            Open trace
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                    {itemResult ? (
                      <div className="stack compact-stack eval-pack-guidance">
                        <strong>Selected result outcome</strong>
                        <p>
                          {outcomeLabel(itemResult.normalized_outcome)}
                          {selectedOutcomeDetail(item, itemResult) ? ` · ${selectedOutcomeDetail(item, itemResult)}` : ''}
                          {typeof itemResult.raw_score === 'number' ? ` · Score ${itemResult.raw_score}` : ''}
                        </p>
                        {selectedOutcomeExplanation(item, itemResult) ? (
                          <div className="eval-pack-guidance-block">
                            <strong>What this means</strong>
                            <p>{selectedOutcomeExplanation(item, itemResult)}</p>
                          </div>
                        ) : null}
                        {itemResult.notes ? (
                          <div className="eval-pack-guidance-block">
                            <strong>Notes</strong>
                            <p>{itemResult.notes}</p>
                          </div>
                        ) : null}
                        <ResultPayloadBlock title="Source payload" payload={itemResult.source_payload} />
                      </div>
                    ) : null}
                    {reviewContext ? (
                      <ReviewContextBlock reviewContext={reviewContext} />
                    ) : guidance ? (
                      <div className="stack compact-stack eval-pack-guidance">
                        <strong>{expectedBehaviorHeading(item.validation_mode)}</strong>
                        <p>{guidance.description}</p>
                        {guidance.steps.length ? (
                          <div className="eval-pack-guidance-block">
                            <strong>What to do</strong>
                            <ol className="eval-pack-guidance-list">
                              {guidance.steps.map((step) => (
                                <li key={step}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        ) : null}
                        {guidance.criteria.length ? (
                          <div className="eval-pack-guidance-block">
                            <strong>How to decide</strong>
                            <ul className="eval-pack-guidance-list">
                              {guidance.criteria.map((criterion) => (
                                <li key={criterion}>{criterion}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No pack items yet</strong>
              <p>This pack has not persisted any prepared validation cases yet.</p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
